import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { VerifiedUser, CheckCircle, RemoveCircleOutline } from '@mui/icons-material';
import { status as statusColors, text } from '../../theme/colors';
import { formatHarareDateShort } from '../../utils/time';

/**
 * The buyer's side of the Eqaya Verification: what was checked, by which
 * authority, when, and by whom. It renders only what the public detail payload
 * carries (`verification_panel`, `deed_validated_si76`, `conveyancer.firm`) —
 * a buyer abroad is being asked to trust this page, so nothing here is implied.
 */

const OUTCOME = {
  verified: { label: 'Verified', color: statusColors.success, bg: statusColors.successBg, Icon: CheckCircle },
  not_applicable: { label: 'Not applicable', color: statusColors.neutral, bg: statusColors.neutralBg, Icon: RemoveCircleOutline },
};

/**
 * A status the API grew after this page shipped must never be dressed up as
 * "Not applicable" — that is a claim about the property, and the wrong one.
 * Unknown statuses render their own value in neutral colours instead, tidied
 * from the API's token form ("needs_more_info") into something a buyer can read.
 */
const humanise = (s) => {
  const words = String(s).replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

const outcomeFor = (s) => OUTCOME[s] || (s
  ? { label: humanise(s), color: statusColors.neutral, bg: statusColors.neutralBg, Icon: RemoveCircleOutline }
  : null);

export default function VerificationBadgePanel({ panel, deedValidatedSi76, conveyancerFirm }) {
  if (!panel) return null;
  const verifiedOn = formatHarareDateShort(panel.verified_at);
  const visitOn = formatHarareDateShort(panel.site_visit_date);
  const items = Array.isArray(panel.items) ? panel.items : [];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderColor: statusColors.success, bgcolor: statusColors.successBg }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <VerifiedUser sx={{ color: statusColors.success }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: text.primary }}>
          {verifiedOn ? `Eqaya Verified on ${verifiedOn}` : 'Eqaya Verified'}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
        Eqaya checked this property independently of the agent before it was listed. Here is what was checked.
      </Typography>

      {items.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {items.map((i) => {
            const o = outcomeFor(i.status);
            return (
              <Stack key={i.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {o && (
                  <Chip
                    size="small"
                    icon={<o.Icon sx={{ color: `${o.color} !important` }} />}
                    label={o.label}
                    sx={{ color: o.color, bgcolor: o.bg, fontWeight: 600 }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>{i.label}</Typography>
                {i.authority && (
                  <Typography variant="caption" sx={{ color: text.tertiary }}>({i.authority})</Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Box>
        {(visitOn || panel.verifier_display_name) && (
          <Typography variant="body2" sx={{ color: text.secondary }}>
            {visitOn ? `Site visit on ${visitOn}` : 'Site visit completed'}
            {panel.verifier_display_name
              ? ` by Eqaya verifier ${panel.verifier_display_name}${panel.verifier_display_name.endsWith('.') ? '' : '.'}`
              : '.'}
          </Typography>
        )}
        {deedValidatedSi76 && (
          <Typography variant="body2" sx={{ color: text.secondary }}>
            Title deed validated under SI 76 of 2020.
          </Typography>
        )}
        {conveyancerFirm && (
          <Typography variant="body2" sx={{ color: text.secondary }}>
            Conveyancer: {conveyancerFirm}.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
